import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Tooltip, IconButton, Typography } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import DateRangeFilter from "../components/common/DateRangeFilter";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import AppButton from "../components/buttons/AppButton";
import CreateCreditNoteModal from "../components/creditNotes/CreateCreditNoteModal";

import useDebounce from "../hooks/useDebounce";
import useWorkspaceNavigation from "../hooks/useWorkspaceNavigation";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { getCreditNotes } from "../redux/actions/creditNoteActions";
import StatusChip from "../components/common/StatusChip";
import { CreditNoteSummaryDto } from "../shared/dtos/creditNote/CreditNoteSummaryDto";
import { formatPlainNumber } from "../utils/appUtils";

const CreditNotesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { navigateToTab } = useWorkspaceNavigation();
  const { hasPermission } = usePermissions();
  const today = new Date().toISOString().split("T")[0];

  const {
    items: creditNotes,
    pagination,
    loadingFetchAll: loadingCreditNotes,
    errorFetchAll: errorLoadingCreditNotes,
  } = useAppSelector((state) => state.creditNote);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);
  const [startDate, setStartDate] = useState<string>(today);
  const [endDate, setEndDate] = useState<string>(today);
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false);

  const canRead = hasPermission("credit_note:read_all");
  const canCreate = hasPermission("credit_note:create");

  const fetchNotes = useCallback(() => {
    if (!canRead) return;
    dispatch(
      getCreditNotes({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch,
        startDate,
        endDate,
      }),
    );
  }, [dispatch, pageConfig, debouncedSearch, startDate, endDate, canRead]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  useEffect(() => {
    setPageConfig((p) => ({ ...p, page: 1 }));
  }, [debouncedSearch, startDate, endDate]);

  // --- Table Configuration with Column Management ---
  const columns: TableColumn<CreditNoteSummaryDto>[] = useMemo(
    () => [
      {
        id: "noteNumber",
        label: "C.N. #",
        minWidth: 120,
        format: (_, row) => (
          <b style={{ color: "#f72585" }}>{row.noteNumber}</b>
        ),
      },
      {
        id: "issueDate",
        label: "Date",
        minWidth: 120,
        format: (_, row) => new Date(row.issueDate).toLocaleDateString(),
      },
      {
        id: "customerName",
        label: "Customer",
        minWidth: 150,
        format: (_, row) => row.customerName || "Walk-in",
      },
      { id: "reason", label: "Reason", minWidth: 120 },
      {
        id: "status",
        label: "Status",
        minWidth: 100,
        format: (_, row) => <StatusChip status={row.status} />,
      },
      { id: "currencyCode", label: "CUR", minWidth: 60, align: "center" },
      {
        id: "total",
        label: "Total",
        minWidth: 120,
        align: "right",
        format: (_, row) => (
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {formatPlainNumber(row.total)}
          </Typography>
        ),
      },
      {
        id: "amountUsed",
        label: "Used",
        minWidth: 120,
        align: "right",
        format: (_, row) => (
          <Typography
            variant="body2"
            color="secondary.main"
            sx={{ fontWeight: "bold" }}
          >
            {formatPlainNumber(row.amountUsed)}
          </Typography>
        ),
      },

      // --- HIDDEN BY DEFAULT ---
      {
        id: "transactionStatus",
        label: "Txn Status",
        minWidth: 110,
        defaultHidden: true,
      },
      {
        id: "subtotal",
        label: "Subtotal",
        minWidth: 100,
        defaultHidden: true,
        align: "right",
        format: (_, row) => formatPlainNumber(row.subtotal),
      },
      {
        id: "totalTaxableAmount",
        label: "Taxable",
        minWidth: 100,
        defaultHidden: true,
        align: "right",
        format: (_, row) => formatPlainNumber(row.totalTaxableAmount),
      },
      {
        id: "totalTax",
        label: "Tax",
        minWidth: 100,
        defaultHidden: true,
        align: "right",
        format: (_, row) => formatPlainNumber(row.totalTax),
      },
      {
        id: "exchangeRate",
        label: "Rate",
        minWidth: 80,
        defaultHidden: true,
        align: "right",
        format: (_, row) => formatPlainNumber(row.exchangeRate),
      },
    ],
    [],
  );

  return (
    <PageContainer
      title="Credit Notes & Returns"
      subtitle="Manage customer returns, store credits, and refunds."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setIsCreateOpen(true)}
          >
            New Credit Note
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search by C.N # or Customer..."
      >
        <DateRangeFilter
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </PageToolbar>

      <StandardTable
        columns={columns}
        data={creditNotes}
        loading={loadingCreditNotes}
        error={errorLoadingCreditNotes}
        onRetry={fetchNotes}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(p) => setPageConfig((c) => ({ ...c, page: p }))}
        onRowsPerPageChange={(l) =>
          setPageConfig((c) => ({ ...c, limit: l, page: 1 }))
        }
        enableColumnManagement={true}
        rowAction={(row) => (
          <Tooltip title="View Credit Note">
            <IconButton
              size="small"
              color="primary"
              onClick={() => navigateToTab(`/credit-notes/${row.id}`)}
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      />
      {isCreateOpen && (
        <CreateCreditNoteModal
          open={isCreateOpen}
          onClose={() => {
            setIsCreateOpen(false);
            fetchNotes();
          }}
        />
      )}
    </PageContainer>
  );
};

export default CreditNotesPage;
